import React, { useEffect, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { useNavigate } from 'react-router-dom';
import api from '../../api';
import {
  GetHumanTasksResponse,
  AcceptHumanTaskResponse,
  HumanTask,
} from '@dreamai/types';
import './human-tasks.scss';

// Simple inline SVG icons
const PhoneIcon = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <path d='M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z' />
  </svg>
);

const GlobeIcon = () => (
  <svg
    width='14'
    height='14'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <circle cx='12' cy='12' r='10' />
    <line x1='2' y1='12' x2='22' y2='12' />
    <path d='M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z' />
  </svg>
);

const ChevronRightIcon = () => (
  <svg
    width='16'
    height='16'
    viewBox='0 0 24 24'
    fill='none'
    stroke='currentColor'
    strokeWidth='2.5'
    strokeLinecap='round'
    strokeLinejoin='round'
  >
    <polyline points='9 18 15 12 9 6' />
  </svg>
);

const HumanTasks: React.FC = () => {
  const { getAccessTokenSilently } = useAuth0();
  const navigate = useNavigate();
  const [activeTasks, setActiveTasks] = useState<HumanTask[]>([]);
  const [availableTasks, setAvailableTasks] = useState<HumanTask[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [acceptingTaskId, setAcceptingTaskId] = useState<string | null>(null);
  const [accessDenied, setAccessDenied] = useState<boolean>(false);

  useEffect(() => {
    loadTasks();
  }, []);

  const loadTasks = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = await getAccessTokenSilently();

      // Fetch all tasks
      const response = await api.get<GetHumanTasksResponse>('/human-tasks', {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.tasks) {
        // Separate into active (accepted by current user) and available (pending)
        const active = response.data.tasks.filter(
          (task) => task.status === 'accepted' || task.status === 'in_progress',
        );
        const available = response.data.tasks.filter(
          (task) => task.status === 'pending',
        );

        // Sort by deadline (soonest first), tasks without deadline go last
        const sortByDeadline = (a: HumanTask, b: HumanTask) => {
          if (!a.deadline && !b.deadline) return 0;
          if (!a.deadline) return 1;
          if (!b.deadline) return -1;
          return (
            new Date(a.deadline).getTime() - new Date(b.deadline).getTime()
          );
        };

        setActiveTasks(active.sort(sortByDeadline));
        setAvailableTasks(available.sort(sortByDeadline));
      } else {
        setError('Failed to load tasks');
      }
    } catch (err: any) {
      console.error('Error loading tasks:', err);
      // Check if it's a 403 forbidden error (no access)
      if (err.response?.status === 403) {
        setAccessDenied(true);
      } else {
        setError(err.response?.data?.error || 'Failed to load tasks');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptTask = async (taskId: string) => {
    try {
      setAcceptingTaskId(taskId);
      const token = await getAccessTokenSilently();

      const response = await api.post<AcceptHumanTaskResponse>(
        `/human-tasks/${taskId}/accept`,
        {},
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      if (
        response.data.success &&
        response.data.threadId &&
        response.data.task
      ) {
        // Navigate to the thread - use 'other' as agent_id since ops users
        // don't have the task creator's agent in their agent list
        navigate(
          `/conversations?agent_id=other&thread_id=${response.data.threadId}`,
        );
      } else {
        setError('Failed to accept task');
      }
    } catch (err: any) {
      console.error('Error accepting task:', err);
      setError(err.response?.data?.error || 'Failed to accept task');
    } finally {
      setAcceptingTaskId(null);
    }
  };

  const handleOpenThread = (threadId: string) => {
    // Use 'other' as agent_id since ops users access agents from other companies
    navigate(`/conversations?agent_id=other&thread_id=${threadId}`);
  };

  const formatTaskType = (taskType: string) => {
    if (taskType === 'phone_call') return 'Phone Call';
    if (taskType === 'web_action') return 'Web Action';
    return taskType;
  };

  const formatDeadline = (deadline: string | undefined) => {
    if (!deadline) return null;
    const date = new Date(deadline);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffHours = diffMs / (1000 * 60 * 60);

    // Format based on urgency
    if (diffHours < 0) {
      return { text: 'Overdue', className: 'deadline-overdue' };
    } else if (diffHours < 2) {
      return {
        text: `Due in ${Math.ceil(diffHours * 60)} min`,
        className: 'deadline-urgent',
      };
    } else if (diffHours < 24) {
      return {
        text: `Due in ${Math.ceil(diffHours)}h`,
        className: 'deadline-soon',
      };
    } else {
      return {
        text: date.toLocaleDateString('en-GB', {
          day: 'numeric',
          month: 'short',
        }),
        className: 'deadline-normal',
      };
    }
  };

  if (loading) {
    return (
      <div className='page-wrapper'>
        <div className='page-inner-wrapper human-tasks-page'>
          <div className='loading-state'>Loading tasks...</div>
        </div>
      </div>
    );
  }

  if (accessDenied) {
    return (
      <div className='page-wrapper'>
        <div className='page-inner-wrapper human-tasks-page'>
          <div className='access-denied-state'>
            <h3>Access Denied</h3>
            <p>This page is only accessible to internal ops and super users.</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className='page-wrapper'>
        <div className='page-inner-wrapper human-tasks-page'>
          <div className='error-state'>{error}</div>
        </div>
      </div>
    );
  }

  const TaskTypeIcon = ({ taskType }: { taskType: string }) => {
    if (taskType === 'phone_call') return <PhoneIcon />;
    if (taskType === 'web_action') return <GlobeIcon />;
    return null;
  };

  return (
    <div className='page-wrapper'>
      <div className='page-inner-wrapper human-tasks-page'>
        <div className='page-header-area'>
          <div className='title-and-text'>
            <h3>Human Tasks</h3>
          </div>
        </div>

        <div className='tasks-container'>
          {/* Available Tasks Section - Left Column */}
          <div className='tasks-section available-section'>
            <h4 className='section-title'>Available</h4>
            {availableTasks.length === 0 ? (
              <div className='empty-state'>No tasks available</div>
            ) : (
              <div className='tasks-list'>
                {availableTasks.map((task) => {
                  const deadlineInfo = formatDeadline((task as any).deadline);
                  return (
                    <div key={task.id} className='task-row available-task'>
                      <div className='task-type-icon'>
                        <TaskTypeIcon taskType={task.taskType} />
                      </div>
                      <div className='task-info'>
                        <span className='task-title'>{task.title}</span>
                        {deadlineInfo ? (
                          <span
                            className={`task-deadline ${deadlineInfo.className}`}
                          >
                            {deadlineInfo.text}
                          </span>
                        ) : (
                          <span className='task-date'>
                            {new Date(task.createdAt).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                      <button
                        className='accept-btn'
                        onClick={() => handleAcceptTask(task.id)}
                        disabled={acceptingTaskId === task.id}
                        title='Accept Task'
                      >
                        {acceptingTaskId === task.id ? (
                          <span className='loading-spinner' />
                        ) : (
                          <ChevronRightIcon />
                        )}
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Active Tasks Section - Right Column */}
          <div className='tasks-section active-section'>
            <h4 className='section-title'>Active</h4>
            {activeTasks.length === 0 ? (
              <div className='empty-state'>No active tasks</div>
            ) : (
              <div className='tasks-list'>
                {activeTasks.map((task) => {
                  const deadlineInfo = formatDeadline((task as any).deadline);
                  return (
                    <div
                      key={task.id}
                      className='task-row active-task'
                      onClick={() =>
                        task.threadId && handleOpenThread(task.threadId)
                      }
                    >
                      <div className='task-type-icon'>
                        <TaskTypeIcon taskType={task.taskType} />
                      </div>
                      <div className='task-info'>
                        <span className='task-title'>{task.title}</span>
                        {deadlineInfo ? (
                          <span
                            className={`task-deadline ${deadlineInfo.className}`}
                          >
                            {deadlineInfo.text}
                          </span>
                        ) : (
                          <span className='task-status-badge'>
                            {task.status}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default HumanTasks;
